import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Separator,
  Text,
} from '@/components/ui';
import {
  IconChevronDown,
  IconChevronRight,
  IconCircleX,
} from '@tabler/icons-react';

export interface TerminalErrorItem {
  id: string;
  label: string;
  message: string;
  prefix?: string; // Optional prefix like "Column Name → " or "Form #123"
  metadata?: {
    [key: string]: unknown;
  };
}

export interface TerminalErrorGroup {
  id: string;
  label: React.ReactNode;
  errors: TerminalErrorItem[];
  badge?: React.ReactNode;
}

export interface TerminalErrorDisplayProps {
  title?: React.ReactNode;
  groups: TerminalErrorGroup[];
  className?: string;
  defaultExpanded?: boolean;
}

export const TerminalErrorDisplay: React.FC<TerminalErrorDisplayProps> = ({
  title,
  groups,
  className,
  defaultExpanded = false,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    defaultExpanded ? new Set(groups.map(g => g.id)) : new Set()
  );

  const toggleGroup = (groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const totalErrors = useMemo(
    () => groups.reduce((sum, group) => sum + group.errors.length, 0),
    [groups]
  );

  if (groups.length === 0 || totalErrors === 0) {
    return null;
  }

  return (
    <Box className={`terminal-error-display ${className || ''}`}>
      {title && <Box className="terminal-error-display__header">{title}</Box>}

      <Flex direction="column" gap="1" className="terminal-error-display__list">
        {groups.map(group => {
          const isExpanded = expandedGroups.has(group.id);

          return (
            <Card
              key={group.id}
              variant="surface"
              size="none"
              className="terminal-error-display__card"
            >
              <Flex direction="column">
                {/* Group header - collapsible */}
                <Flex
                  align="center"
                  gap="2"
                  className="terminal-error-display__card-header"
                >
                  <Button
                    variant="text"
                    isIconOnly
                    size="small"
                    onClick={() => toggleGroup(group.id)}
                    className="terminal-error-display__toggle-button"
                  >
                    {isExpanded ? <IconChevronDown /> : <IconChevronRight />}
                  </Button>
                  <Flex
                    align="center"
                    gap="2"
                    className="terminal-error-display__card-header-content"
                  >
                    <Text
                      size="2xsmall"
                      weight="bold"
                      color="error"
                      className="terminal-error-display__group-label"
                      as="div"
                    >
                      {group.label}
                    </Text>
                    {group.badge || (
                      <Badge color="red" variant="soft" size="xsmall">
                        {group.errors.length}
                      </Badge>
                    )}
                  </Flex>
                </Flex>

                {/* Error messages - terminal style */}
                {isExpanded && (
                  <>
                    <Separator />
                    <Box className="terminal-error-display__message-container">
                      {group.errors.map((error, idx) => (
                        <Flex
                          key={error.id || idx}
                          align="start"
                          gap="2"
                          className="terminal-error-display__message-row"
                        >
                          {/* Error icon */}
                          <IconCircleX className="terminal-error-display__error-icon" />

                          {/* Error message */}
                          <Box className="terminal-error-display__message-content">
                            {error.prefix && (
                              <Text
                                as="span"
                                className="terminal-error-display__prefix"
                              >
                                {error.prefix}
                              </Text>
                            )}
                            <Text
                              as="span"
                              className="terminal-error-display__label"
                            >
                              {error.label}
                            </Text>
                            <Text
                              as="span"
                              className="terminal-error-display__separator"
                            >
                              :{' '}
                            </Text>
                            <Text
                              as="span"
                              className="terminal-error-display__message"
                            >
                              {error.message}
                            </Text>
                          </Box>
                        </Flex>
                      ))}
                    </Box>
                  </>
                )}
              </Flex>
            </Card>
          );
        })}
      </Flex>
    </Box>
  );
};
